import { Fragment } from "react";
import styled from "styled-components";
import { mobile } from "../utilities/responsive";

import designImg from "../assets/B-design.jpg";
import design2Img from "../assets/b-design2.jpg";
import teachImg from "../assets/b-teach.jpg";
import codeImg from "../assets/b-coder.jpg";
import swatchImg from "../assets/design-swatches.jpg";
import Overline from "../Components/Overline";

const cards = [
  {
    id: "c1",
    img: codeImg,
    company: "Bend Logic",
    title: "Founding Engineer",
    text: "Hands-on lead developer in charge of designing and implementing architecture decisions. Communicated progress, obstacles, and expectations to stakeholders, enabling leadership to make well-informed decisions on the company's technical direction.",
  },
  {
    id: "c2",
    img: designImg,
    company: "Arch Public",
    title: "Senior Developer",
    text: "Built and maintained an e-commerce marketplace, including UI/UX and back end server and database architecture. Designed and implemented a CI/CD pipeline with robust testing to expedite the software delivery process and improve security.",
  },
  {
    id: "c3",
    img: teachImg,
    company: "DoD Platform One",
    title: "Onboarding Lead",
    text: "Onboarded teams to Platform One, the official DoD DevSecOps Enterprise Services. Trained teams in the adoption of Agile/XP practices, and consulted on the integration of modern technologies in secure DoD environments.",
  },
  {
    id: "c4",
    img: design2Img,
    company: "Galvanize",
    title: "Enterprise Associate, Full Stack Developer",
    text: "Consulted with global enterprises on the adoption of modern engineering technology, development practices, and the re-skilling of their existing workforce.",
  },
  {
    id: "c5",
    img: swatchImg,
    company: "What Else Have I Done?",
    title: "Too Much",
    text: "I've built software for Fortune 500s like USAA and Volkswagen, worked inside secure DoD environments for the Air Force and defense contractors, and taught software engineering for programs like Hack Reactor. I've also taught elementary schoolers about wild animals and bugs — and somewhere in there, I co-founded an improv comedy troupe called Stitch Tactics. They're still around!",
  },
];

const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  position: relative; // Necessary for Overline component positioning
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, #ebe6dc 0px, #eeeeea 420px, #eeeeea 100%);

  ${mobile({
    margin: "auto",
    height: "auto",
    padding: "60px 0 40px",
    background: "linear-gradient(to bottom, #ebe6dc 0px, #eeeeea 280px, #eeeeea 100%)",
  })}
`;

const Wrapper = styled.div`
  height: 400px;
  display: flex;
  flex-wrap: nowrap;
  justify-content: start;
  font-family: Arial, Helvetica, sans-serif;

  ${mobile({
    marginTop: "60px",
    height: "auto",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px"
  })}
`;

const Card = styled.label`
  width: 80px;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  overflow: hidden;
  border-radius: 2rem;
  margin: 0 10px;
  display: flex;
  align-items: flex-end;
  transition: 0.6s cubic-bezier(0.28, -0.03, 0, 0.99);
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.8);
  background-image: url(${(props) => props.$img});

  ${mobile({
    width: "min(90vw, calc(100vw - 46px))",
    minHeight: "80px",
    height: "80px",
    alignItems: "flex-start",
    margin: "5px 0 5px 30px"
  })}
`;

const Row = styled.div`
  color: white;
  display: flex;
  flex-wrap: nowrap;
  background: rgba(10, 10, 12, 0.38);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 -12px 32px -8px rgba(0, 0, 0, 0.35);

  ${mobile({
    flexDirection: "column",
  })}
`;

const Icon = styled.div`
  background: #233;
  color: white;
  border-radius: 50%;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 15px;
  height: 50px;
  align-self: center;

  ${mobile({
    height: "50px",
  })}
`;

const Description = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  height: 260px;
  width: min(520px, calc(100vw - 590px));
  box-sizing: border-box;
  padding: 0 24px 0 0;
  opacity: 0;
  transform: translateY(30px);
  transition-delay: .3s;
  transition: all .3s ease;

  ${mobile({
    height: "auto",
    width: "90%",
    padding: "25px",
    justifyContent: "flex-end",
  })}

`;

const Radio = styled.input`
  display: none;

  &:checked + ${Card}{
    width: min(600px, calc(100vw - 480px));

    ${mobile({
      height: "550px",
      minHeight: "450px",
      width: "min(90vw, calc(100vw - 46px))"
    })}
  }
  &:checked + ${Card} ${Description} {
    opacity: 1 !important;
    transform: translateY(0) !important;
  }
  &:checked + ${Card} ${Row} {
    ${mobile({
      alignSelf: "flex-end",
      width: "100%",
    })}
  }
`;

const Heading = styled.h4`
  text-transform: uppercase;
  margin: 0;

  ${mobile({marginBottom: "1vh" })}
`;

const Company = styled.span`
  display: block;
  color: #55b7ff;
`;

const JobTitle = styled.span`
  display: block;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  margin-top: 3px;
`;

const Text = styled.p`
  color: #e8e8ee;
  font-size: 1rem;
  line-height: 1.5;
  margin: 6px 0 0;

  ${mobile({
    marginBottom: "calc(10vw + 2vh + 1vmin)",
    fontSize: "0.85rem",
  })}
`;

const Slider = () => {
  return (
    <Container>
      <Overline text="Experience" />
      <Wrapper>
        {cards.map((card, i) => (
          <Fragment key={card.id}>
            <Radio type="radio" name="slide" id={card.id} defaultChecked={i === 0} />
            <Card htmlFor={card.id} $img={card.img}>
              <Row>
                <Icon>{i + 1}</Icon>
                <Description>
                  <Heading>
                    <Company>{card.company}</Company>
                    <JobTitle>{card.title}</JobTitle>
                  </Heading>
                  <Text>{card.text}</Text>
                </Description>
              </Row>
            </Card>
          </Fragment>
        ))}
      </Wrapper>
    </Container>
  );
};

export default Slider;
